import { formatPrice, formatPercent2, formatSignedPercent } from "../lib/format";

/**
 * The percentages the compact block prints, already stated by the server in the same quote as the
 * prices beside them. Every one is measured from the entry price except the two `away` distances,
 * which are measured from the marker and say so on the label. The component never derives one:
 * a missing value prints no percentage rather than a percentage with an unnamed base.
 */
export interface ScalePercents {
  /** Signed, from the entry price. */
  lower: string | null;
  upper: string | null;
  lowerStop: string | null;
  upperStop: string | null;
  /** Magnitudes, from the marker price to that stop. */
  lowerStopAway: string | null;
  upperStopAway: string | null;
  /** Signed, the marker against the entry price. */
  marker: string | null;
  /** How far past the crossed edge the marker sits; a magnitude, with the edge named. */
  beyond: { side: "lower" | "upper"; percent: string } | null;
}

/**
 * The share of the axis a caption such as "now · +7.45% since entry" needs to sit on one line.
 * It only chooses which side a caption leans towards; the width cap that follows keeps it inside
 * the block either way, so an imprecise value can make a caption wrap but never overflow.
 */
const CAPTION_ROOM = 25;
interface PriceScaleProps {
  min: string;
  max: string;
  current: string | null;
  lowerStop?: string;
  upperStop?: string;
  preliminary?: boolean;
  compact?: boolean;
  mini?: boolean;
  closed?: boolean;
  inactiveStops?: boolean;
  entry?: string;
  /** Compact branch only; absent leaves every label with its price and role and no percentage. */
  percents?: ScalePercents | null;
  /** A second, transient marker — e.g. price while scrubbing a position's history — distinct from
   * `current`. Rendered as its own line; does not replace or relabel the closing/live marker. */
  scrubPrice?: string | null;
  unit: string;
  /**
   * Overrides the mini figcaption label text only; titles keep the full value. Returning `null`
   * means "no override for this value" — the caller can defer to the default sibling-aware
   * `compactDisplay` on a per-value basis instead of losing precision it cannot recover (e.g.
   * truncating a sub-1 price to "0.00").
   */
  labelFormatter?: (value: string) => string | null;
}

export function PriceScale({
  min,
  max,
  current,
  lowerStop,
  upperStop,
  preliminary,
  compact,
  mini,
  closed = false,
  inactiveStops = false,
  entry,
  percents,
  scrubPrice,
  unit,
  labelFormatter,
}: PriceScaleProps) {
  const currentValue = current ?? "";
  const markerIsValid =
    currentValue !== "" && Number.isFinite(Number(currentValue)) && Number(currentValue) > 0;
  const entryIsValid = entry !== undefined && Number.isFinite(Number(entry)) && Number(entry) > 0;
  const scrubValue = scrubPrice ?? "";
  const scrubIsValid =
    scrubValue !== "" && Number.isFinite(Number(scrubValue)) && Number(scrubValue) > 0;
  const values = [
    lowerStop,
    min,
    markerIsValid ? currentValue : undefined,
    // The mini entry tick is a passenger on the existing axis: it never moves or rescales a mark.
    entryIsValid && !mini ? entry : undefined,
    scrubIsValid ? scrubValue : undefined,
    max,
    upperStop,
  ]
    .filter((value): value is string => value !== "" && value != null)
    .map(Number);
  const valid =
    (closed || mini ? current === null || markerIsValid : markerIsValid) &&
    values.length >= 2 &&
    values.every(Number.isFinite) &&
    values.every((value) => value > 0) &&
    Number(max) > Number(min);
  if (!valid) {
    return (
      <div className="price-scale unavailable" role="img" aria-label="Price scale unavailable">
        — · Price data not provided
      </div>
    );
  }
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low || 1;
  const posOf = (value: string) => Math.max(0, Math.min(100, ((Number(value) - low) / span) * 100));
  const pos = (value: string) => `${String(posOf(value))}%`;
  const offset = (value: string) => {
    if (!markerIsValid) return null;
    const n = (Number(value) / Number(currentValue) - 1) * 100;
    return (n >= 0 ? "+" : "") + n.toFixed(1) + "%";
  };
  const siblingPrices = [
    lowerStop,
    min,
    markerIsValid ? currentValue : undefined,
    max,
    upperStop,
  ].filter((value): value is string => !!value);
  const display = (value: string) =>
    formatPrice(
      value,
      "long",
      siblingPrices.filter((sibling) => sibling !== value),
    );
  const compactDisplay = (value: string) => {
    const formatted = display(value);
    return /e/i.test(formatted) ? value : formatted;
  };
  const miniLabel = (value: string) => labelFormatter?.(value) ?? compactDisplay(value);
  // `labelFormatter` may itself return `null` (see the prop doc) to defer to `compactDisplay` for
  // that one value; `??` above already covers both "no formatter" and "formatter said null".
  const description = [
    lowerStop ? `lower stop ${display(lowerStop)}` : null,
    `LP minimum ${display(min)}`,
    markerIsValid
      ? `${closed ? "closing" : "current"} price ${display(currentValue)}`
      : closed
        ? "closing price not recorded"
        : null,
    `LP maximum ${display(max)}`,
    upperStop ? `upper stop ${display(upperStop)}` : null,
    entryIsValid ? `entry price ${display(entry)}` : null,
    unit,
  ]
    .filter(Boolean)
    .join(", ");
  const fullClassName = [
    "price-scale",
    compact && "compact",
    closed && "closed",
    inactiveStops && "inactive-stops",
    markerIsValid &&
      (Number(currentValue) < Number(min) || Number(currentValue) > Number(max)) &&
      "outside-range",
  ]
    .filter(Boolean)
    .join(" ");
  if (mini) {
    // Left to right in price order, like the track above them: an out-of-range current price
    // sits at the end it crossed, not between the bounds.
    const captions: { role: "min" | "current" | "max"; value: string }[] = [
      { role: "min" as const, value: min },
      ...(markerIsValid ? [{ role: "current" as const, value: currentValue }] : []),
      { role: "max" as const, value: max },
    ].sort((a, b) => Number(a.value) - Number(b.value));
    // An entry off this axis (e.g. beyond a re-armed stop) is simply not drawn, never clamped.
    const entryOnScale = entryIsValid && Number(entry) >= low && Number(entry) <= high;
    return (
      <figure
        className={`mini-price-scale ${preliminary ? "planned" : ""}`}
        aria-label={description}
      >
        <div className="scale-track" aria-hidden="true">
          <span
            className="range-band"
            style={{ left: pos(min), width: `calc(${pos(max)} - ${pos(min)})` }}
          />
          {lowerStop && <span className="stop-line" style={{ left: pos(lowerStop) }} />}
          {upperStop && <span className="stop-line" style={{ left: pos(upperStop) }} />}
          {entryOnScale && <span className="entry-line" style={{ left: pos(entry) }} />}
          {markerIsValid && <span className="current-line" style={{ left: pos(currentValue) }} />}
          {scrubIsValid && <span className="scrub-line" style={{ left: pos(scrubValue) }} />}
        </div>
        <figcaption>
          {markerIsValid ? (
            captions.map(({ role, value }) => (
              <span key={role} className={role === "current" ? "current" : undefined} title={value}>
                {miniLabel(value)}
              </span>
            ))
          ) : (
            <>
              <span title={min}>{miniLabel(min)}</span>
              <span title={current ?? undefined}>—</span>
              <span title={max}>{miniLabel(max)}</span>
            </>
          )}
        </figcaption>
      </figure>
    );
  }
  const showFullDetails = !compact;
  if (compact) {
    /*
     * Four rows, down from eight (design screens 42 and 43). Every percentage sits on the mark it
     * describes, so each one is anchored at `pos(price)` rather than pushed to the ends of a
     * space-between row, where a bound's number ended up under a stop's tick. The percentages come
     * from `percents`, already stated by the server in this quote; the prices drive the geometry,
     * exactly as they did before.
     */
    const beyondSide = !markerIsValid
      ? null
      : Number(currentValue) < Number(min)
        ? ("lower" as const)
        : Number(currentValue) > Number(max)
          ? ("upper" as const)
          : null;
    /**
     * Pins a label to the axis end it sits on, so it can never overflow the block.
     *
     * A mark label is NOT bounded by the gap to its nearest neighbour. Capping its width at a
     * share of the axis does not narrow it: the price inside never wraps, so the number simply
     * runs past the cap and out of the block, which the browser test measures as horizontal
     * overflow. Nothing here may measure a rendered width, so there is no cap that knows what the
     * number needs. Two marks close together therefore still print over each other — the
     * near-edge collision open as a design follow-up.
     */
    const anchor = (value: string) => {
      const at = posOf(value);
      if (at <= 0) return { className: "at-start", style: { left: 0 } };
      if (at >= 100) return { className: "at-end", style: { right: 0 } };
      return { className: "", style: { left: `${String(at)}%` } };
    };
    /**
     * Entry and the marker lean away from each other, so their captions never collide. The two
     * ends pin instead of leaning: at `0` a leaning caption would sit entirely to the left of the
     * block, where the card's `overflow: hidden` cuts it — and the axis minimum is exactly where
     * an out-of-range marker lands, the state this caption exists to show.
     *
     * When both sit on the same point — a position whose price has not moved since entry, the
     * commonest state of a fresh one — there is no "away" to lean towards, so `tie` decides it:
     * entry keeps the left of the mark, the marker takes the right. Without that the two captions
     * resolve to the same offset and print on top of each other.
     */
    const lean = (value: string, other: string | null, tie: "left" | "right") => {
      const at = posOf(value);
      if (at <= 0) return { className: "", style: { left: 0, maxWidth: "100%" } };
      if (at >= 100) return { className: "leans-left", style: { right: 0, maxWidth: "100%" } };
      const there = other === null ? null : posOf(other);
      const away = there === null ? true : there === at ? tie === "left" : there > at;
      // A caption near an end has no room on that side long before `at` reaches 0 or 100, and
      // leaning into it lays the price line — which never wraps — outside the block, which the
      // browser test forbids as horizontal overflow. So room decides first and leaning away from
      // the other caption is only the tie-break. Two captions that crowd the same end therefore
      // both turn inwards and can overprint: at an end there is no arrangement that both keeps
      // each caption inside the block and holds them apart, and staying inside wins. That
      // overprint is the near-edge label collision already open as a design follow-up.
      const goLeft = 100 - at < CAPTION_ROOM ? true : at < CAPTION_ROOM ? false : away;
      const room = goLeft ? at : 100 - at;
      // The chosen side is also the width budget, so a caption wider than its room wraps inside
      // the block instead of running past it. Decided from percentages, never from measurement.
      const maxWidth = `${String(room)}%`;
      return goLeft
        ? { className: "leans-left", style: { right: `${String(100 - at)}%`, maxWidth } }
        : { className: "", style: { left: `${String(at)}%`, maxWidth } };
    };
    const entryLean = lean(entryIsValid ? entry : min, markerIsValid ? currentValue : null, "left");
    const markerLean = lean(
      markerIsValid ? currentValue : min,
      entryIsValid ? entry : null,
      "right",
    );
    const labels: {
      key: string;
      price: string;
      percent: string | null;
      role: "range-start" | "range-end" | "auto-exit-stop";
      away: string | null;
    }[] = [
      ...(lowerStop
        ? [
            {
              key: "lower-stop",
              price: lowerStop,
              percent: percents?.lowerStop ?? null,
              role: "auto-exit-stop" as const,
              away: closed ? null : (percents?.lowerStopAway ?? null),
            },
          ]
        : []),
      {
        key: "range-start",
        price: min,
        percent: percents?.lower ?? null,
        role: "range-start" as const,
        away: null,
      },
      {
        key: "range-end",
        price: max,
        percent: percents?.upper ?? null,
        role: "range-end" as const,
        away: null,
      },
      ...(upperStop
        ? [
            {
              key: "upper-stop",
              price: upperStop,
              percent: percents?.upperStop ?? null,
              role: "auto-exit-stop" as const,
              away: closed ? null : (percents?.upperStopAway ?? null),
            },
          ]
        : []),
    ];
    const markerRole = closed ? "closed at" : "now";
    const beyondText =
      beyondSide && percents?.beyond?.side === beyondSide
        ? `${beyondSide === "upper" ? "+" : "−"}${formatPercent2(percents.beyond.percent)} ${
            beyondSide === "upper" ? "above range end" : "below range start"
          }`
        : null;
    const markerCaption = beyondText
      ? `${markerRole} · ${beyondText}`
      : percents?.marker
        ? `${markerRole} · since entry`
        : markerRole;
    return (
      <figure className={fullClassName} aria-label={description}>
        <div className="compact-range-above">
          {entryIsValid && (
            <span className={`compact-entry ${entryLean.className}`} style={entryLean.style}>
              <small>entry</small>
              <b className="num" title={entry}>
                {compactDisplay(entry)}
              </b>
            </span>
          )}
          <span className={`compact-marker ${markerLean.className}`} style={markerLean.style}>
            <small>{markerCaption}</small>
            {markerIsValid ? (
              <b className="num" title={currentValue}>
                {compactDisplay(currentValue)}
                {percents?.marker ? ` · ${formatSignedPercent(percents.marker)}` : ""}
              </b>
            ) : (
              <>
                <b className="unknown">—</b>
                <em>Closing price not provided</em>
              </>
            )}
          </span>
        </div>
        <div className="scale-track" aria-hidden="true">
          <span
            className="range-band"
            style={{ left: pos(min), width: `calc(${pos(max)} - ${pos(min)})` }}
          />
          {/* A closed card states where the close landed in words; it does not keep warning
              about a range that is no longer held, so the striped strip is live-card only. */}
          {!closed && beyondSide === "upper" && (
            <span
              className="beyond-strip"
              style={{ left: pos(max), width: `calc(${pos(currentValue)} - ${pos(max)})` }}
            />
          )}
          {!closed && beyondSide === "lower" && (
            <span
              className="beyond-strip"
              style={{ left: pos(currentValue), width: `calc(${pos(min)} - ${pos(currentValue)})` }}
            />
          )}
          <span className="edge-line" style={{ left: pos(min) }} />
          <span className="edge-line" style={{ left: pos(max) }} />
          {lowerStop && <span className="stop-line below" style={{ left: pos(lowerStop) }} />}
          {upperStop && <span className="stop-line above" style={{ left: pos(upperStop) }} />}
          {entryIsValid && <span className="entry-line" style={{ left: pos(entry) }} />}
          {markerIsValid && <span className="current-line" style={{ left: pos(currentValue) }} />}
          {scrubIsValid && <span className="scrub-line" style={{ left: pos(scrubValue) }} />}
        </div>
        <figcaption className="compact-range-marks" role="region" aria-label="Range marks">
          {labels.map((label) => (
            <span
              key={label.key}
              className={`range-mark ${label.role} ${anchor(label.price).className}`}
              style={anchor(label.price).style}
            >
              {label.percent !== null && (
                <b className="num">{formatSignedPercent(label.percent)}</b>
              )}
              <span className="mark-price num" title={label.price}>
                {compactDisplay(label.price)}
              </span>
              <small>
                {label.role === "range-start" ? (
                  "range start"
                ) : label.role === "range-end" ? (
                  "range end"
                ) : (
                  <>
                    <span className="role-long">auto-exit stop</span>
                    <span className="role-short">stop</span>
                    {label.away !== null && (
                      <span className="away">{` · ${formatPercent2(label.away)} away`}</span>
                    )}
                  </>
                )}
              </small>
            </span>
          ))}
        </figcaption>
        <span className="compact-range-unit" title={unit}>
          {unit}
        </span>
      </figure>
    );
  }
  const lowerCurrentOffset = offset(min);
  const upperCurrentOffset = offset(max);
  return (
    <figure className={fullClassName} aria-label={description}>
      <div className="scale-track">
        <span
          className="range-band"
          style={{ left: pos(min), width: `calc(${pos(max)} - ${pos(min)})` }}
        />
        {lowerStop && <span className="stop-line below" style={{ left: pos(lowerStop) }} />}
        {upperStop && <span className="stop-line above" style={{ left: pos(upperStop) }} />}
        {markerIsValid && <span className="current-line" style={{ left: pos(currentValue) }} />}
      </div>
      <figcaption className="scale-labels">
        {lowerStop && (
          <span style={{ left: pos(lowerStop) }}>
            <b>{display(lowerStop)}</b>
            <small>stop</small>
          </span>
        )}
        <span style={{ left: pos(min) }}>
          <b>{display(min)}</b>
          <small>Lower{lowerCurrentOffset ? ` · ${lowerCurrentOffset}` : ""}</small>
        </span>
        {markerIsValid && (
          <span className="current-label" style={{ left: pos(currentValue) }}>
            <b>{display(currentValue)}</b>
            <small>{closed ? "Closed at" : "Current"}</small>
          </span>
        )}
        <span style={{ left: pos(max) }}>
          <b>{display(max)}</b>
          <small>Upper{upperCurrentOffset ? ` · ${upperCurrentOffset}` : ""}</small>
        </span>
        {upperStop && (
          <span style={{ left: pos(upperStop) }}>
            <b>{display(upperStop)}</b>
            <small>stop</small>
          </span>
        )}
      </figcaption>
      {showFullDetails && (
        <>
          <p className="scale-note">
            {preliminary ? "Preliminary geometry" : "Actual tick-rounded bounds"} · {unit} · Price
            increases →
          </p>
          <details className="exact-values">
            <summary>Exact price values</summary>
            <div className="exact-values-grid">
              {[
                ["LP minimum", min],
                ...(markerIsValid
                  ? [[closed ? "Closing price" : "Current price", currentValue]]
                  : []),
                ["LP maximum", max],
                ...(lowerStop ? [["Lower stop", lowerStop]] : []),
                ...(upperStop ? [["Upper stop", upperStop]] : []),
              ].map(([label, value]) => (
                <label key={label}>
                  {label}
                  <input aria-label={`Exact ${label}`} readOnly value={value} />
                </label>
              ))}
            </div>
          </details>
        </>
      )}
    </figure>
  );
}
